import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { fetchTransaction, refundTransaction } from "../../Apis/Wallet-api";
import { formatDateTime, formatLabel, formatMoney } from "../../Utils/walletFormat";
import { isRefundable } from "../../Utils/walletTransaction";

export default function WalletTransactionDetailPage() {
  const { walletId, transactionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState(false);
  const [txn, setTxn] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundNotes, setRefundNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTxn(await fetchTransaction(walletId, transactionId));
    } catch (err) {
      toast.error(err.response?.data?.message || "Transaction not found.");
      setTxn(null);
    } finally {
      setLoading(false);
    }
  }, [walletId, transactionId]);

  useEffect(() => {
    load();
  }, [load]);

  const runRefund = async () => {
    if (!txn) return;
    setRefunding(true);
    try {
      const result = await refundTransaction(
        walletId,
        txn.id,
        refundAmount,
        refundNotes
      );
      toast.success("Refund applied.");
      const refundId = result?.transaction?.id;
      if (refundId) {
        navigate(`/wallet/transactions/${walletId}/${refundId}`);
      } else {
        setRefundAmount("");
        setRefundNotes("");
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Refund failed.");
    } finally {
      setRefunding(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!txn) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Transaction not found.</Typography>
        <Button onClick={() => navigate("/wallet/transactions")}>Back</Button>
      </Box>
    );
  }

  const completedRefunds = (txn.refunds || []).filter(
    (r) => r.status === "completed"
  );
  const refundable = isRefundable(txn);

  const fields = [
    ["ID", txn.id],
    ["Wallet ID", txn.wallet_id],
    ["Type", formatLabel(txn.type)],
    ["Status", formatLabel(txn.status)],
    ["Amount", formatMoney(txn.amount, txn.currency)],
    ["Currency", txn.currency],
    [
      "Reference txn",
      txn.reference_transaction_id ? (
        <Link
          component={RouterLink}
          to={`/wallet/transactions/${walletId}/${txn.reference_transaction_id}`}
        >
          #{txn.reference_transaction_id}
        </Link>
      ) : (
        "—"
      ),
    ],
    ["Gateway order", txn.gateway_order_id || "—"],
    ["Gateway reference", txn.gateway_reference || "—"],
    ["Initiated by", formatLabel(txn.initiated_by)],
    ["Initiated by user", txn.initiated_by_user_id || "—"],
    ["Notes", txn.notes || "—"],
    ["Created", formatDateTime(txn.created_at)],
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 640 }}>
      <Stack spacing={2}>
        <Button size="small" onClick={() => navigate("/wallet/transactions")}>
          ← Back to transactions
        </Button>
        <Typography variant="h5">Transaction #{txn.id}</Typography>
        <Chip label={formatLabel(txn.status)} size="small" />
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            {fields.map(([label, value]) => (
              <Stack key={label} direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2" component="div">
                  {value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>

        {completedRefunds.length > 0 && (
          <Alert severity="info" variant="outlined">
            Refund recorded:{" "}
            {completedRefunds.map((r, i) => (
              <React.Fragment key={r.id}>
                {i > 0 ? ", " : null}
                <Link
                  component={RouterLink}
                  to={`/wallet/transactions/${walletId}/${r.id}`}
                >
                  #{r.id} ({formatMoney(r.amount)})
                </Link>
              </React.Fragment>
            ))}
          </Alert>
        )}

        {refundable && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1">Refund this debit</Typography>
              <Typography variant="body2" color="text.secondary">
                Restores balance and credit split from the original debit.
                Leave amount empty for a full refund of{" "}
                {formatMoney(txn.amount, txn.currency)}.
              </Typography>
              <TextField
                label="Refund amount (optional)"
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                fullWidth
                size="small"
                helperText={`Max ${formatMoney(txn.amount, txn.currency)}`}
              />
              <TextField
                label="Notes"
                value={refundNotes}
                onChange={(e) => setRefundNotes(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
              <Divider />
              <Button
                variant="contained"
                color="warning"
                disabled={refunding}
                onClick={runRefund}
              >
                {refunding ? "Processing…" : "Apply refund"}
              </Button>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
